import React from 'react'
import { View } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { connect } from 'react-redux'

import { PrimaryTextButton } from 'APP/Components/Buttons'
import CarePlanListItem from 'APP/Components/CarePlanListItem'
import Typography from 'APP/Converse/Typography'

import { useSelector } from 'APP/Hooks/redux'
import I18n from 'APP/I18n'
import { CanadianRegion, RootScreens } from 'APP/Lib/Enums'
import { TabScreens } from 'APP/Lib/ContentHelpers'
import {
  selectEpharmacyExternalServicesByProvider,
  selectPatientRegion,
} from 'APP/Redux/PatientRedux'
import { usePillwayNavigation } from 'APP/Lib/PillwayHelpers'

import Styles from './style'
import { ResponsiveFlatList } from 'APP/Converse/Layout'
import Button from 'APP/Converse/Button'
import Analytics from 'APP/Services/Analytics'
import type { AppNavigation } from 'APP/Nav/types'
import type { RootState } from 'APP/Store/CreateStore'
import type {
  RecentCarePlansHeaderProps,
  NoCarePlansProps,
  PillwayCarePlanData,
  ProcessedCarePlanItem,
  ProcessedItem,
  RecentCarePlansProps,
  RecentCarePlansStateProps,
  RenderItemPayload,
} from './types'

const RecentCarePlansHeader = ({ showNavButton }: RecentCarePlansHeaderProps) => {
  const navigation = useNavigation() as AppNavigation
  return (
    <View style={Styles.headerContainer} testID={'RECENT_CARE_PLANS_HEADER'}>
      <Typography style={Styles.header}>{I18n.t('RecentCarePlans.header')}</Typography>
      {showNavButton && (
        <PrimaryTextButton
          analyticsName="RecentCarePlans.seeAll"
          onPress={() => {
            navigation.navigate(RootScreens.chat, { tabName: TabScreens.chat.carePlans })
          }}
          containerStyle={Styles.seeAllButtonContainer}
          disabledContainerStyle={Styles.seeAllButtonContainer}
        >
          {I18n.t('RecentCarePlans.headerButton')}
        </PrimaryTextButton>
      )}
    </View>
  )
}

const NoCarePlans = ({ fromChatScreen, goToTabByName }: NoCarePlansProps) => {
  const navigation = useNavigation() as AppNavigation

  const handleOnPress = () => {
    if (fromChatScreen) {
      goToTabByName?.(TabScreens.chat.getCare)
    } else {
      navigation.navigate(RootScreens.chat, { tabName: TabScreens.chat.getCare })
    }
  }

  return (
    <View style={Styles.cardContainer}>
      <Typography style={Styles.noCarePlansBody}>
        {I18n.t('RecentCarePlans.noCarePlansBody')}
      </Typography>
      <Button
        variant="oomph"
        title={I18n.t('RecentCarePlans.noCarePlansButton') ?? ''}
        onPress={handleOnPress}
        analyticsName="RecentCarePlans.getCare"
      />
    </View>
  )
}

export const usePillwayCarePlanData = (): PillwayCarePlanData | null => {
  const patientRegion = useSelector(selectPatientRegion)
  const pillwayEpharmacies = useSelector((state) =>
    selectEpharmacyExternalServicesByProvider(state, 'pillway')
  )
  const navigateToPillway = usePillwayNavigation()

  if (patientRegion === CanadianRegion.QC) {
    return null
  }
  if (!pillwayEpharmacies || pillwayEpharmacies.length < 1) {
    return null
  }

  return {
    title: I18n.t('PillwayCarePlans.epharmacy.title') ?? '',
    description: I18n.t('PillwayCarePlans.epharmacy.description') ?? '',
    onPressHandler: () => {
      Analytics.trackEvent('button_click', {
        button_value: 'PillwayCarePlans.epharmacy',
      })
      navigateToPillway(pillwayEpharmacies[0], 'care-plans')
    },
  }
}

const RecentCarePlans = ({
  carePlans,
  episodes,
  style,
  fromChatScreen = false,
  goToTabByName,
  route,
}: RecentCarePlansProps) => {
  let processedCarePlans: ProcessedItem[] = ((carePlans as { episode_id: string }[])
    ?.slice(...(fromChatScreen ? [0] : [0, 3]))
    ?.map((carePlan) => ({
      carePlan,
      episode: episodes[carePlan.episode_id] as ProcessedCarePlanItem['episode'],
    })) ?? []) as ProcessedItem[]

  const pillwayCarePlan = usePillwayCarePlanData()
  if (pillwayCarePlan && route?.name !== 'todayTab') {
    processedCarePlans = [pillwayCarePlan, ...processedCarePlans]
  }

  return (
    <ResponsiveFlatList
      style={style}
      data={processedCarePlans}
      keyExtractor={(item) => String(item?.carePlan?.id)}
      renderItem={({ item }: { item: RenderItemPayload }) => (
        <CarePlanListItem
          carePlan={item.carePlan}
          episode={item.episode}
          title={item.title}
          description={item.description}
          onPressHandler={item.onPressHandler}
          disabled={false}
          style={undefined}
        />
      )}
      ListHeaderComponent={() =>
        fromChatScreen ? null : (
          <RecentCarePlansHeader showNavButton={processedCarePlans.length > 0} />
        )
      }
      ListEmptyComponent={
        <NoCarePlans fromChatScreen={fromChatScreen} goToTabByName={goToTabByName} />
      }
    />
  )
}

const mapStateToProps = (state: RootState): RecentCarePlansStateProps => {
  const { history } = state
  return {
    episodes: history.episodes,
    carePlans: history.completedCarePlans,
  }
}

export default connect(mapStateToProps)(RecentCarePlans)
